// Cart drawer behaviours, loaded from snippets/cart-drawer.liquid.
// Lived inline in that snippet until 2026-09: 41 KB re-sent in every page's
// HTML and in every /?sections=cart-drawer refresh, never cached.
// Kaching-look drawer behaviors: per-item variant swap, upsell quick-add,
// in-place drawer refresh, header count sync, and free-shipping-bar
// animation across EVERY update path (theme quantity/remove renders too).
(function () {
  function cartDrawerEl() { return document.querySelector('cart-drawer'); }

  // ---- Drawer images ------------------------------------------------------
  // No image box in the drawer may be on screen before its image. Two parts:
  //
  // 1. Warm-up: download the exact URLs the drawer will render before the add
  //    lands. The line thumbnail is a Shopify image transform that nothing
  //    else on a product or collection page requests, and on a cold CDN edge
  //    its first request waits 400 to 800ms for the resize, so without this
  //    the new row showed an empty square for half a second. Triggers:
  //    - product page, at idle after load: data-warm-thumb on <cart-drawer>
  //      (snippets/cart-drawer.liquid), plus again on the form's submit for a
  //      variant whose image differs (data-warm-thumbs);
  //    - a card's add controls hovered, focused or touched ([data-recs-product]):
  //      their data-cart-thumb if set, else the card's first gallery image
  //      (data-pdp-gallery, snippets/card-product.liquid);
  //    - a drawer recommendation card hovered, focused or touched: data-thumb
  //      on its form or option (assets/cart-recs-engine.js).
  //    The payment icons ride along while the cart is empty (data-warm-icons).
  //    The Image objects are kept, so the decoded bitmaps stay in the memory
  //    cache and the drawer's <img> tags with the same URLs are complete the
  //    moment they are inserted.
  // 2. Readiness: the drawer opens (assets/cart-drawer.js renderContents) or
  //    swaps in new rows (refreshCartDrawer below) only once its images are
  //    decoded, capped at IMG_WAIT_MS so one stalled image can never hold it.
  //    With the warm-up this costs nothing; it only waits when the add came
  //    faster than the warm-up.
  const IMG_WAIT_MS = 500;
  const warmed = new Map();
  function warmImage(key, setup) {
    if (!key || warmed.has(key)) return;
    const img = new Image();
    // A detached image starts at Low priority and never gets raised; these
    // are a few KB and needed next, ahead of the card warm-up's big photos.
    try { img.fetchPriority = 'high'; } catch (e) {}
    setup(img);
    warmed.set(key, img);
    if (img.decode) img.decode().catch(() => {});
  }
  // base: an image_url with no width ("//lovestore.co/cdn/shop/files/x.jpg?v=1").
  // Must stay byte for byte the line thumbnail's srcset in
  // snippets/cart-drawer.liquid (also sections/main-cart.liquid's), or the
  // warm-up downloads a file the drawer never asks for.
  function thumbSrcset(base) { return base + '&width=100 1x, ' + base + '&width=200 2x'; }
  // Keyed by the srcset string, as preloadImagesOf() keys a drawer tag, so
  // both find the same Image.
  function warmThumb(base) {
    if (!base || base.indexOf('?v=') < 0) return;
    const srcset = thumbSrcset(base);
    warmImage(srcset, (img) => { img.srcset = srcset; img.src = base + '&width=100'; });
  }
  function warmIcons() {
    const d = cartDrawerEl();
    const list = d && d.dataset.warmIcons;
    if (!list) return;
    list.split(' ').forEach((u) => { if (u) warmImage(u, (img) => { img.src = u; }); });
  }

  // Product page: the likely add is this product; warm once the page's own
  // images have had the network (load, then idle).
  (function warmPageProduct() {
    const run = () => {
      const d = cartDrawerEl();
      if (!d || !d.dataset.warmThumb) return;
      warmThumb(d.dataset.warmThumb);
      warmIcons();
    };
    const later = () => {
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2000 });
      else setTimeout(run, 300);
    };
    if (document.readyState === 'complete') later();
    else window.addEventListener('load', later, { once: true });
  })();

  // Product form submit: warm the chosen variant's thumbnail if idle has not
  // come yet or its image differs. Only for this page's product; another
  // product's form (a quick-view popup) falls back on the readiness wait.
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || !form.closest || !form.closest('product-form')) return;
    const d = cartDrawerEl();
    if (!d || !d.dataset.warmThumb) return;
    const id = String(new FormData(form).get('id') || '');
    if (!id || (',' + (d.dataset.warmVariants || '')).indexOf(',' + id + ',') < 0) return;
    let base = d.dataset.warmThumb;
    (d.dataset.warmThumbs || '').split('|').forEach((pair) => {
      const [vid, url] = pair.split('~');
      if (vid === id && url) base = url;
    });
    warmThumb(base);
    warmIcons();
  }, true);

  // Add intent on a product card or a drawer recommendation card.
  function onAddIntent(e) {
    const t = e.target;
    if (!t || !t.closest) return;
    const upsell = t.closest('.cart-upsell-card');
    if (upsell) {
      const form = upsell.querySelector('[data-cart-upsell-form]');
      const sel = form && form.querySelector('select[name="id"]');
      const opt = sel && sel.selectedOptions && sel.selectedOptions[0];
      warmThumb((opt && opt.dataset.thumb) || (form && form.dataset.thumb));
      return;
    }
    const intent = t.closest('[data-recs-product]');
    if (!intent) return;
    // data-cart-thumb (the variant's own image, when the card provides it)
    // wins; else the card's first gallery image, which is item.image for
    // every product whose variants carry no image of their own.
    let base = intent.dataset.cartThumb;
    if (!base) {
      const card = intent.closest('.card-container') || intent.closest('.card');
      const gallery = card && (card.matches('[data-pdp-gallery]') ? card : card.querySelector('[data-pdp-gallery]'));
      const first = gallery && (gallery.dataset.pdpGallery || '').split('|')[0];
      base = first && first.split('~')[0];
    }
    if (!base) return;
    warmThumb(base);
    warmIcons();
  }
  ['pointerover', 'focusin', 'touchstart'].forEach((t) => document.addEventListener(t, onAddIntent, { capture: true, passive: true }));

  // Resolves once every eager <img> under root is decoded, or after maxMs.
  function imagesReady(root, maxMs) {
    const imgs = root ? [...root.querySelectorAll('img')].filter((i) => i.loading !== 'lazy') : [];
    if (!imgs.length) return Promise.resolve();
    const all = Promise.all(imgs.map((i) => (i.decode ? i.decode().catch(() => {}) : null)));
    return Promise.race([all, new Promise((r) => setTimeout(r, maxMs))]);
  }
  // For markup that is not in the page yet (a parsed section): download and
  // decode its images off-screen, so the swapped-in tags are complete at once.
  function preloadImagesOf(root, maxMs) {
    const imgs = root ? [...root.querySelectorAll('img')].filter((i) => i.getAttribute('loading') !== 'lazy') : [];
    const waits = [];
    for (const tag of imgs) {
      const src = tag.getAttribute('src') || '';
      const srcset = tag.getAttribute('srcset') || '';
      const sizes = tag.getAttribute('sizes') || '';
      const key = srcset || src;
      if (!key) continue;
      warmImage(key, (img) => {
        if (sizes) img.sizes = sizes;
        if (srcset) img.srcset = srcset;
        if (src) img.src = src;
      });
      const img = warmed.get(key);
      if (img && img.decode) waits.push(img.decode().catch(() => {}));
    }
    if (!waits.length) return Promise.resolve();
    return Promise.race([Promise.all(waits), new Promise((r) => setTimeout(r, maxMs))]);
  }
  // Used by CartDrawer.renderContents (assets/cart-drawer.js) before open().
  window.breyCartDrawerImagesReady = (root) => imagesReady(root, IMG_WAIT_MS);

  // Rows currently playing their exit animation. The re-render replaces the
  // whole list with innerHTML, and a replacement row mounts WITHOUT
  // is-removing — so a swap landing mid-exit doesn't interrupt the animation,
  // it erases it. With one item you never notice: the refresh is the last
  // thing that happens. Removing 4-5 in a row, each removal's refresh lands
  // while the NEXT row is still animating, and that one just disappears.
  let removingRows = 0;
  function noteRemovalStart() { removingRows++; }
  function noteRemovalEnd() { if (removingRows > 0) removingRows--; }
  // Gate the destructive swap, not the fetch: the network request still runs
  // in parallel, only the DOM write waits. Capped so a missed animationend
  // (interrupted animation, reduced motion) can never wedge the drawer.
  function waitForRemovals(maxMs) {
    if (removingRows <= 0) return Promise.resolve();
    const deadline = Date.now() + (maxMs || 900);
    return new Promise((resolve) => {
      (function tick() {
        if (removingRows <= 0 || Date.now() > deadline) return resolve();
        setTimeout(tick, 40);
      })();
    });
  }

  async function refreshCartDrawer(cart) {
    const res = await fetch('/?sections=cart-drawer');
    const html = (await res.json())['cart-drawer'];
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const freshInner = doc.querySelector('.drawer__inner');
    const currentInner = document.querySelector('cart-drawer .drawer__inner');
    if (!freshInner || !currentInner) throw new Error('drawer refresh failed');
    // swap only the inner content: the theme's CartDrawer keeps cached
    // references and its focus trap on the outer nodes
    // Carry the shipping bar across the swap ONLY when a cart came with it:
    // its ~450ms transition is still running and applyCartTotals below will
    // write the new value. Without a cart there is nothing to write, so the
    // fresh server-rendered bar has to win or the bar would never move.
    const keepBar = cart ? currentInner.querySelector('.free-shipping-bar') : null;
    // A new row's thumbnail (an add from a card or a drawer recommendation)
    // must be ready before it is swapped in: the drawer can already be open,
    // and there is no fade to hide an empty box. Usually warm already.
    await preloadImagesOf(freshInner, IMG_WAIT_MS);
    // Everything above is read-only; this is the first destructive step, so
    // it is the right place to wait for any row still on its way out.
    await waitForRemovals();
    currentInner.innerHTML = freshInner.innerHTML;
    if (keepBar) {
      const freshBar = currentInner.querySelector('.free-shipping-bar');
      if (freshBar) freshBar.replaceWith(keepBar);
    }
    const freshDrawer = doc.querySelector('cart-drawer');
    const cur = cartDrawerEl();
    if (freshDrawer && cur) cur.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
    // A cart payload means we already hold the counts — spending a third
    // round trip on /cart.js just to read them back was pure latency.
    if (cart) applyCartTotals(cart);
    else syncHeaderCount();
    // after the bar's animation, never during it
    setTimeout(fitAllFsbText, 700);
  }

  // Paint totals straight from a mutation's own cart payload. Everything
  // here used to wait for the section re-render, so the count, subtotal and
  // shipping bar all lagged the click by a second.
  const fmtMoney = (cents) => {
    const n = (cents / 100).toFixed(2).split('.');
    return n[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + n[1] + '€';
  };
  // Removals defer their re-render past the exit animations (row 420ms, bar
  // 450ms) so a 46KB innerHTML parse doesn't drop frames mid-flight. Two
  // quick removals would otherwise queue two overlapping re-renders, so only
  // the last one survives — it reads the cart fresh anyway.
  let structuralTimer = null;
  function scheduleStructuralRefresh(cart) {
    clearTimeout(structuralTimer);
    structuralTimer = setTimeout(() => {
      structuralTimer = null;
      refreshCartDrawer(cart).catch(() => {});
    }, 500);
  }

  // Cart mutations must not overlap. Shopify's Ajax cart has no per-line
  // concurrency control, so two /cart/change.js calls in flight together
  // clobber each other's result — remove three rows quickly and one of them
  // silently does not happen. Each change waits for the previous to settle.
  let cartQueue = Promise.resolve();
  function queueCartChange(fn) {
    const run = cartQueue.then(fn, fn);
    cartQueue = run.catch(() => {});
    return run;
  }

  // pending stepper requests, keyed by input — see the click handler
  const qtyTimers = new WeakMap();

  // Old line out, new line in — a swap rather than a pop of text that had
  // already silently changed.
  // One swap at a time. The totals patch and the re-render replay can both
  // reach this, and a re-render mid-sequence can re-apply it to a fresh
  // node, which played the message animation twice.
  function fsbSwapBusy(text) {
    if (text.dataset.fsbSwapping) return true;
    text.dataset.fsbSwapping = '1';
    setTimeout(() => { delete text.dataset.fsbSwapping; }, 900);
    return false;
  }
  function swapFsbText(text, paint) {
    if (!text) { paint(); return; }
    if (fsbSwapBusy(text)) { paint(); return; }
    text.classList.remove('fsb__text--in');
    text.classList.add('fsb__text--out');
    setTimeout(() => {
      paint();
      text.classList.remove('fsb__text--out');
      void text.offsetWidth;
      text.classList.add('fsb__text--in');
      setTimeout(() => text.classList.remove('fsb__text--in'), 640);
    }, 200);
  }

  // Free shipping just unlocked. Fires ONLY on the crossing, never on a
  // cart that was already complete, so it can't repeat on every edit.
  let fsbCelebratePending = false;
  // Latch on the STATE, not a cooldown. Both the totals patch and the
  // re-render replay can spot the same crossing, and they can be seconds
  // apart, so any time window is guesswork. markFsbComplete flips this the
  // moment either one sees completion, and only a genuine drop back below
  // re-arms it.
  let fsbComplete = null;
  function markFsbComplete(isComplete) {
    let dir = null;
    if (fsbComplete !== null && isComplete !== fsbComplete) dir = isComplete ? 'up' : 'down';
    fsbComplete = isComplete;
    return dir;
  }
  function celebrateFsb(bar) {
    if (!bar) return;
    // Adding from a product card crosses the threshold while the drawer is
    // still closed, so the whole sequence used to play to an empty room and
    // the shopper saw nothing. Hold it until the drawer is actually open.
    const drawer = cartDrawerEl();
    if (!drawer || !drawer.classList.contains('active')) {
      fsbCelebratePending = true;
      return;
    }
    fsbCelebratePending = false;
    bar.classList.remove('fsb--celebrate');
    void bar.offsetWidth; // restart the keyframes
    bar.classList.add('fsb--celebrate');
    setTimeout(() => bar.classList.remove('fsb--celebrate'), 620);
  }
  // The mirror of celebrateFsb, minus the held-until-visible behaviour:
  // losing the discount off-screen is not worth replaying at the shopper.
  // The knob is pinned to the track's RIGHT END (right: -2px), so the two
  // directions are not symmetric. Going up the fill has to travel to reach it,
  // which is why the celebration waits ~300ms. Going down the fill detaches
  // from the knob immediately, so reusing that delay landed the pop long after
  // the bar had left the circle. This is roughly the time the fill takes to
  // clear the 15px knob at the start of the 620ms ease.
  // Keep the free-shipping message on ONE line. The amount changes with the
  // cart and the unlocked message is longer again, so no single font-size is
  // right for every string — measure the rendered width and step down rather
  // than let it wrap (the truck emoji dropping to a second line looked broken).
  // Runs from the stylesheet size every time, so it grows back when the text
  // gets shorter instead of staying stuck small.
  function fitFsbText(text) {
    if (!text) return;
    // setProperty with 'important': the stylesheet sets font-size with
    // !important, and a plain inline style loses to it -- the size computed
    // here would be ignored and the text would still overflow.
    text.style.removeProperty('font-size');
    const avail = text.clientWidth;
    if (!avail) return; // drawer closed / not laid out yet
    const natural = text.scrollWidth;
    if (natural <= avail + 0.5) return; // already fits: no write, no reflow
    // Solve for the size in ONE step. The previous version stepped down 0.5px
    // at a time, and each step wrote a font-size then read scrollWidth — up to
    // 40 forced synchronous layouts of the whole drawer, fired from paintText
    // at exactly the moment the shipping bar animates. That is what made the
    // bar stutter or drop frames on an add.
    const base = parseFloat(getComputedStyle(text).fontSize) || 14;
    const size = Math.max(9, Math.floor(base * (avail / natural) * 10) / 10);
    text.style.setProperty('font-size', size + 'px', 'important');
  }
  function fitAllFsbText() {
    document.querySelectorAll('cart-drawer .fsb__text').forEach(fitFsbText);
  }
  window.addEventListener('resize', () => {
    clearTimeout(window.__fsbFitT);
    window.__fsbFitT = setTimeout(fitAllFsbText, 120);
  });

  const FSB_KNOB_EXIT_MS = 110;

  function regressFsb(bar) {
    if (!bar) return;
    const drawer = cartDrawerEl();
    if (!drawer || !drawer.classList.contains('active')) return;
    bar.classList.remove('fsb--lose');
    void bar.offsetWidth;
    bar.classList.add('fsb--lose');
    setTimeout(() => bar.classList.remove('fsb--lose'), 620);
  }
  function swapFsbTextDown(text, paint) {
    if (!text) { paint(); return; }
    if (fsbSwapBusy(text)) { paint(); return; }
    text.classList.remove('fsb__text--settle');
    text.classList.add('fsb__text--fall');
    setTimeout(() => {
      paint();
      text.classList.remove('fsb__text--fall');
      void text.offsetWidth;
      text.classList.add('fsb__text--settle');
      setTimeout(() => text.classList.remove('fsb__text--settle'), 560);
    }, 200);
  }
  function flushPendingCelebration() {
    if (!fsbCelebratePending) return;
    setTimeout(() => {
      const b = document.querySelector('cart-drawer .free-shipping-bar');
      if (b) celebrateFsb(b);
    }, 260); // let the drawer finish sliding in first
  }
  // Watch the drawer itself rather than relying on one code path to call the
  // flush. A crossing detected while it was closed is consumed by the latch,
  // so if nothing replays it the celebration is simply lost — which is what
  // made it fire only sometimes.
  (function watchDrawerOpen() {
    const el = cartDrawerEl();
    if (!el) { setTimeout(watchDrawerOpen, 300); return; }
    new MutationObserver((ms) => {
      for (const m of ms) {
        const wasActive = (m.oldValue || '').includes('active');
        if (!wasActive && el.classList.contains('active')) {
          if (pendingFillPlay) {
            const play = pendingFillPlay;
            pendingFillPlay = null;
            // let the drawer finish sliding in first, then run the bar
            setTimeout(play, 260);
          }
          flushPendingCelebration();
          // no layout while closed, so this is the first chance to measure
          setTimeout(fitAllFsbText, 60);
        }
      }
    }).observe(el, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });
  })();

  function applyCartTotals(cart) {
    if (!cart || typeof cart.total_price !== 'number') return;
    // only touch what actually changed, so an unrelated refresh stays still
    const setText = (el, val) => {
      // whitespace-insensitive: Liquid renders prices with newlines inside
      // <bdi>, which made an unchanged value look changed and bump
      const same = (a, b) => String(a).replace(/\s+/g, '') === String(b).replace(/\s+/g, '');
      if (!el || same(el.textContent, val)) return;
      el.textContent = val;
      el.classList.remove('is-bumped');
      void el.offsetWidth; // restart the keyframes
      el.classList.add('is-bumped');
    };
    setText(document.querySelector('cart-drawer .drawer__heading'), 'Cesta \u2022 (' + cart.item_count + ')');
    document.querySelectorAll('.cart__count, .cart-icon-bubble-count').forEach((el) => {
      setText(el, String(cart.item_count));
    });
    setText(document.querySelector('cart-drawer .totals__subtotal-value'), fmtMoney(cart.total_price));

    // Rows too, straight from the server's own line data. Without this the
    // quantity and line price could only change when the section HTML came
    // back — a second round trip for numbers we were already holding.
    // A discounted line shows allocation rows we don't patch, so a patched
    // update would leave them stale. Rare here, but silently wrong is worse
    // than slow: fall back to a full re-render.
    if ((cart.items || []).some((i) => (i.line_level_discount_allocations || []).length)) {
      scheduleStructuralRefresh(cart);
    }
    document.querySelectorAll('cart-drawer .cart-item').forEach((row) => {
      const input = row.querySelector('.quantity__input');
      // Match by line key, never by position. A variant swap prepends the new
      // line, so every index shifts by one — matching positionally wrote each
      // row the next line's price, and every row animated as if it changed.
      const key = row.dataset.lineKey;
      const item = key && cart.items && cart.items.find((i) => i.key === key);
      if (!item || !input) return;
      // A response can land after the shopper has clicked again. That newer
      // click owns the input, so writing the older server value here made
      // the number jump back for a beat before the next response corrected
      // it. If a request is still queued for this row, leave it alone.
      if (qtyTimers.has(input)) return;
      if (String(item.quantity) !== input.value) {
        input.value = item.quantity;
        input.classList.remove('is-bumped');
        void input.offsetWidth;
        input.classList.add('is-bumped');
      }
      const minus = row.querySelector('.quantity__button[name="minus"]');
      if (minus) {
        minus.classList.toggle('quantity__button--remove', item.quantity === 1);
        const lbl = minus.querySelector('[data-qty-label]');
        if (lbl) lbl.textContent = item.quantity === 1 ? lbl.dataset.labelRemove : lbl.dataset.labelDecrease;
      }
      // the variant swap re-adds at this quantity; stale here means a size
      // change would silently change the quantity too
      const vsel = row.querySelector('.cart-item__variant-select');
      if (vsel) vsel.dataset.qty = item.quantity;
      const priceEl = row.querySelector(
        '.cart-item__price-wrapper bdi, .cart-item__price-wrapper .price, .cart-item__price-wrapper'
      );
      setText(priceEl, fmtMoney(item.final_line_price));
    });

    const bar = document.querySelector('cart-drawer .free-shipping-bar');
    if (!bar) return;
    const min = parseInt(bar.dataset.fsbMin, 10);
    if (!min) return;
    const rest = min - cart.total_price;
    const text = bar.querySelector('.fsb__text');
    const paintText = () => {
      if (!text) return;
      if (rest <= 0) {
        text.textContent = bar.dataset.fsbComplete || text.textContent;
        text.classList.add('fsb__text--complete');
      } else {
        text.innerHTML = (bar.dataset.fsbProgress || '').replace('__AMT__', fmtMoney(rest));
        text.classList.remove('fsb__text--complete');
      }
      fitFsbText(text);
    };
    const fill = bar.querySelector('[data-fsb-fill]');
    if (fill) {
      const scale = Math.min(1, cart.total_price / min);
      const dir = markFsbComplete(scale >= 1);
      fill.style.transform = 'scaleX(' + scale + ')';
      // The replay below animates FROM lastFsbScale. This path writes the
      // scale without going through the observer, so leaving it stale made the
      // next re-render replay from an old (often higher) value — the bar
      // jumped up and then ran backwards. Keep the two in step.
      lastFsbScale = scale;
      if (dir === 'down') {
        // bar first, then the message — running them together read as the
        // bar animating after text that had already settled
        setTimeout(() => { regressFsb(bar); swapFsbTextDown(text, paintText); }, FSB_KNOB_EXIT_MS);
      } else if (dir === 'up') {
        // Hold the old message until the fill lands. Swapping it the instant
        // the click registers, then animating it later, read backwards — the
        // swap IS the moment, so it happens on the beat.
        // one beat: the bar and the message move together. They were
        // staggered to stop the bar looking late, but the real cause was the
        // bar's animation outlasting the text's — the durations match now.
        setTimeout(() => { celebrateFsb(bar); swapFsbText(text, paintText); }, 300);
      } else {
        paintText();
      }
    } else {
      paintText();
      // tell the replay this value is already animated, so the re-render
      // that follows doesn't run the same slide a second time
      lastFsbScale = scale;
    }
    const knob = bar.querySelector('.fsb__knob');
    if (knob) knob.classList.toggle('fsb__knob--complete', rest <= 0);
  }

  // Re-queried on call: refreshCartDrawer swaps .drawer__inner's contents, so
  // a reference held across it points into a detached tree.
  function openDrawerIfRequested(form) {
    if (!form || !form.hasAttribute('data-open-drawer')) return;
    const drawer = cartDrawerEl();
    if (!drawer || !drawer.isConnected || typeof drawer.open !== 'function') return;
    if (drawer.classList.contains('active')) { flushPendingCelebration(); return; }
    drawer.open();
    flushPendingCelebration();
  }

  async function syncHeaderCount() {
    try {
      const c = await (await fetch('/cart.js')).json();
      document.querySelectorAll('.cart__count, .cart-icon-bubble-count').forEach((el) => {
        el.textContent = c.item_count;
      });
    } catch (e) {}
  }

  // Free-shipping fill: replay the width transition whenever ANY re-render
  // (ours or the theme's quantity/remove path) repaints the bar.
  // Compared as a NUMBER, never as a string: Liquid writes 1.0 where JS would
  // write 1.000, and that mismatch alone used to make the bar replay an
  // animation it had already finished.
  // The bar's scale has TWO writers: applyCartTotals (optimistic, straight
  // from a mutation response) and the MutationObserver replay below (after a
  // re-render). Both share lastFsbScale, which is the value the replay
  // animates FROM, so a new writer MUST keep it current or the next replay
  // starts from a stale value and the bar runs backwards.
  //
  // It must also advance only when the bar actually moves, never at the
  // moment a change is detected: an add causes two re-renders (the theme's
  // renderContents, then ours), and advancing early made the second pass see
  // from == target and skip the animation entirely.
  //
  // PLAN.md carries the standing note to collapse these into one writer.
  let lastFsbScale = null;
  // a fill animation held back until the drawer is visible
  let pendingFillPlay = null;

  // Testing this by hand: fetch('/cart/add.js') does NOT re-render the drawer
  // — only the real form submit path does — and clearing the cart without
  // reloading leaves lastFsbScale seeded from the old cart. Both produce a
  // convincing but false "the animation is broken".
  const fsbScaleOf = (el) => {
    const m = el && /scaleX\(([-\d.]+)\)/.exec(el.style.transform || '');
    return m ? parseFloat(m[1]) : null;
  };
  const seedFsb = () => {
    const bar = document.querySelector('cart-drawer .free-shipping-bar');
    if (!bar) return;
    const fill = bar.querySelector('[data-fsb-fill]');
    if (lastFsbScale === null) lastFsbScale = fsbScaleOf(fill);
    // Completion is seeded from the KNOB, never from the fill's transform.
    // The replay observer runs before this and PARKS the fill at its start
    // value — scaleX(0) — ready to animate in when the drawer opens. Reading
    // the transform back therefore latched fsbComplete=false on a cart that
    // already qualified; play() then moved the fill to 1 without touching the
    // latch, so the next ordinary cart change looked like a fresh crossing
    // and replayed the entire unlock celebration. That is the red flash on
    // the first removal from a large cart. The knob class is server-rendered
    // straight from rest_to_free_shipping and nothing animates it.
    fsbComplete = !!bar.querySelector('.fsb__knob--complete');
  };
  new MutationObserver(() => {
    const fill = document.querySelector('cart-drawer [data-fsb-fill]');
    if (!fill) return;
    if (fill.dataset.fsbSeen) return;
    fill.dataset.fsbSeen = '1';
    const target = fsbScaleOf(fill);
    if (target === null) return;
    const fsbDir = markFsbComplete(target >= 1);
    if (fsbDir === 'down') {
      setTimeout(() => regressFsb(fill.closest('.free-shipping-bar')), FSB_KNOB_EXIT_MS);
    }
    if (fsbDir === 'up') {
      // server-rendered text is already correct on this path, so just the
      // entrance — no swap needed
      setTimeout(() => {
        const b = fill.closest('.free-shipping-bar');
        celebrateFsb(b);
        const tx = b && b.querySelector('.fsb__text');
        if (tx && !fsbSwapBusy(tx)) {
          tx.classList.remove('fsb__text--in');
          void tx.offsetWidth;
          tx.classList.add('fsb__text--in');
          setTimeout(() => tx.classList.remove('fsb__text--in'), 640);
        }
      }, 300);
    }
    // No previous bar means the cart was empty and none was rendered — the
    // first add then had nothing to animate from and the drawer opened with
    // the fill already in place. Start that case at zero.
    const from = lastFsbScale === null ? 0 : lastFsbScale;
    if (Math.abs(from - target) > 0.001) {
      // Park the fill at the starting value straight away, with no
      // transition, so it is in the right place whether or not anyone can see
      // it yet.
      fill.style.transition = 'none';
      fill.style.transform = 'scaleX(' + from + ')';
      void fill.offsetWidth;
      // Only RUN the animation once the drawer is actually on screen. The
      // re-render lands before the drawer finishes opening, so starting here
      // meant the bar had already reached its final width by the time it came
      // into view — which is why the first add never appeared to animate.
      const play = () => {
        // Re-resolve the fill: an add produces TWO re-renders (the theme's
        // renderContents, then ours), so the node captured here can already be
        // detached by the time the animation runs — the animation then played
        // on a node nobody could see while the live bar sat at full width.
        const f = document.querySelector('cart-drawer [data-fsb-fill]') || fill;
        f.style.transition = '';
        f.style.transform = 'scaleX(' + target + ')';
        // Only now is this what the viewer actually sees.
        lastFsbScale = target;
      };
      const drawerEl = cartDrawerEl();
      if (drawerEl && drawerEl.classList.contains('active')) {
        setTimeout(play, 20);
      } else {
        pendingFillPlay = play;
      }
    } else {
      // nothing to animate — the displayed value is already the target
      lastFsbScale = target;
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState !== 'loading') seedFsb();
  else document.addEventListener('DOMContentLoaded', seedFsb);

  // Own the drawer's +/- steppers outright (capture + stopPropagation, so the
  // theme's own handler never runs).
  //
  // The theme fired one /cart/change per click and re-rendered the drawer
  // after each. Click fast and those overlap: the re-render resets the input
  // mid-flight, so a later request carries a quantity computed from a stale
  // base — which is where "solo puedes añadir 1 de este artículo" came from.
  // It was never about the item; it was a losing race.
  //
  // Instead: step the number locally on every click and debounce ONE change
  // for the value you land on. Fast clicking now costs a single request.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('cart-drawer .quantity__button');
    if (!btn || (btn.name !== 'plus' && btn.name !== 'minus')) return;
    const qty = btn.closest('.quantity');
    const input = qty && qty.querySelector('.quantity__input');
    const line = input && parseInt(input.dataset.index, 10);
    if (!input || !line) return;
    e.preventDefault();
    e.stopPropagation();

    const max = parseInt(input.max, 10);
    const prev = parseInt(input.value, 10) || 0;
    const row = btn.closest('.cart-item');
    let next = prev + (btn.name === 'plus' ? 1 : -1);
    if (next < 0) next = 0;
    if (!isNaN(max) && next > max) next = max;
    if (String(next) === input.value) return;
    if (next > 0) {
      input.value = next;
      // the number changes locally, so it bumps here rather than waiting on
      // the round trip
      input.classList.remove('is-bumped');
      void input.offsetWidth;
      input.classList.add('is-bumped');
    }
    // at 0 the input keeps showing the quantity it had: the row is on its way
    // out, and blanking or zeroing the number mid-animation just looked broken

    if (next === 0) {
      // play the row out now — see base.css
      qty.classList.add('is-removing');
      if (row) {
        row.classList.add('is-removing');
        // Take the space back as soon as the row has slid out, instead of
        // leaving a hole until the re-render lands ~1s later. Done in JS
        // because max-height is ignored on a table cell, so a CSS collapse
        // is inert here.
        noteRemovalStart();
        let settled = false;
        const settle = () => { if (settled) return; settled = true; noteRemovalEnd(); };
        // animationend is not guaranteed: the animation can be interrupted,
        // or absent entirely under prefers-reduced-motion. Without this the
        // counter would never return to zero and every later refresh would
        // eat its 900ms cap.
        setTimeout(settle, 700);
        row.addEventListener('animationend', function done(ev) {
          if (ev.animationName !== 'cart-item-remove') return;
          row.removeEventListener('animationend', done);
          settle();
          // FLIP: note where the rows below sit, hide this one, then play
          // them from their old positions to their new ones. A table row
          // can't be height-animated, so the close-up has to be a transform
          // — otherwise they snap the instant the gap disappears.
          const below = [];
          for (let n = row.nextElementSibling; n; n = n.nextElementSibling) {
            if (n.classList && n.classList.contains('cart-item')) {
              below.push([n, n.getBoundingClientRect().top]);
            }
          }
          row.style.display = 'none';
          for (const [el, prevTop] of below) {
            const delta = prevTop - el.getBoundingClientRect().top;
            if (!delta || !el.animate) continue;
            el.animate(
              [{ transform: 'translateY(' + delta + 'px)' }, { transform: 'none' }],
              { duration: 320, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
            );
          }
        });
      }
    }

    clearTimeout(qtyTimers.get(input));
    const lineKey = row && row.dataset ? row.dataset.lineKey : '';
    qtyTimers.set(input, setTimeout(() => queueCartChange(async () => {
      qtyTimers.delete(input); // this request now owns the row
      let serverCart = null;
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // id (the line item key), NOT line. Line numbers are POSITIONAL:
          // the moment one removal lands, every line below it shifts down by
          // one, so a request queued against the old numbering removes the
          // wrong row — or a line that no longer exists. With several rows
          // removed in a row that is exactly what happened.
          body: JSON.stringify(lineKey ? { id: lineKey, quantity: next } : { line: line, quantity: next })
        });
        if (!res.ok) throw new Error('change failed');
        serverCart = await res.clone().json();
        applyCartTotals(serverCart);
      } catch (err) {
        // fall through: the refresh below re-reads the real cart either way
      }
      // A quantity change is fully described by the response we just got —
      // rows and totals are patched from it — so re-rendering the drawer
      // costs a second round trip and a repaint for nothing. Only a removal
      // changes the structure, and only that still needs the section.
      const structural = next === 0 || !serverCart;
      if (structural) {
        // Let the row exit (420ms) and the bar (450ms) finish first. The
        // re-render is a 46KB innerHTML parse that blocks the main thread,
        // so landing it mid-animation drops frames — that is the stutter,
        // and preserving the bar node alone could not fix it. Nothing is
        // waiting on this: the row is already hidden and the totals are
        // already the server's.
        scheduleStructuralRefresh(serverCart);
      }
    }), 140));
  }, true);

  // The hover strip is rendered INSIDE the product card's <a>, so a real
  // click on a chip activates that link and navigates away instead of
  // adding. Cancel the click's default — which is the navigation AND the
  // button's implicit submit — and raise the submit ourselves, so the
  // handler below still does the AJAX add.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.card-fadd__btn');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    e.stopPropagation();
    const form = btn.closest('form');
    if (!form) return;
    if (form.requestSubmit) form.requestSubmit(btn);
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }, true);

  // Changing a size is add-new + remove-old, because Shopify's Ajax API has
  // no way to swap a line's variant. New lines are PREPENDED, so the item
  // you edited jumped to the top of the cart. There is no reorder endpoint
  // either, so put it back where it was in the DOM — the theme keys its
  // quantity and remove logic off data-index, not row position, so moving
  // a row is safe.
  function restoreLinePosition(variantId, wasAt) {
    if (wasAt < 0) return;
    const rows = [...document.querySelectorAll('cart-drawer .cart-item')];
    const moved = rows.find((r) => {
      const s = r.querySelector('.cart-item__variant-select');
      return s && parseInt(s.value, 10) === variantId;
    });
    if (!moved || rows.indexOf(moved) === wasAt) return;
    const target = rows.filter((r) => r !== moved)[wasAt];
    if (target) target.parentNode.insertBefore(moved, target);
    else moved.parentNode.appendChild(moved);
  }

  // Variant swap. Capture phase + stopPropagation: the theme's CartItems
  // bubble listener would otherwise fire a bogus /cart/change for this
  // select (no data-index) and race an error message into the drawer.
  document.addEventListener('change', async (e) => {
    const sel = e.target.closest('.cart-item__variant-select');
    if (!sel) return;
    e.stopPropagation();
    const key = sel.dataset.itemKey;
    const qty = parseInt(sel.dataset.qty, 10) || 1;
    const newId = parseInt(sel.value, 10);
    // where this line currently sits, so we can put it back (see below)
    const rows = [...document.querySelectorAll('cart-drawer .cart-item')];
    const wasAt = rows.indexOf(sel.closest('.cart-item'));
    sel.disabled = true;
    // Through the same queue as the steppers. This is TWO mutations that must
    // not be interleaved -- a quantity change landing between the add and the
    // remove leaves the cart briefly holding both lines, and restoreLinePosition
    // then puts the row back in the wrong place.
    await queueCartChange(async () => {
    try {
      // ADD FIRST: if the new variant can't be added (sold out, limits),
      // nothing has changed and the old line survives.
      const add = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: parseInt(sel.value, 10), quantity: qty }] }) });
      if (!add.ok) { sel.disabled = false; await refreshCartDrawer(); return; }
      const chg = await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: 0 }) });
      if (chg && chg.ok) { try { applyCartTotals(await chg.clone().json()); } catch (e) {} }
      await refreshCartDrawer();
      restoreLinePosition(newId, wasAt);
    } catch (err) {
      try { await refreshCartDrawer(); } catch (e2) { location.reload(); }
    }
    });
  }, true);

  // Safety net for the add buttons' transient state. The success and failure
  // paths above both clear it, but neither runs if the shopper navigates
  // while /cart/add.js is still in flight -- click a size, click the product,
  // and the chip is frozen mid-add. bfcache then restores that frozen DOM on
  // Back, leaving a chip that is disabled (default cursor, unclickable) and
  // still running the loading sweep. Nothing on the page would ever clear it,
  // so reset every add button whenever a page is shown.
  // Runs on plain loads too, not just persisted ones: harmless there, and it
  // also covers a browser that restores form-control state without bfcache.
  const resetAddButtons = () => {
    document.querySelectorAll('.card-fadd__btn, .cart-upsell-card__add').forEach((b) => {
      b.disabled = false;
      delete b.dataset.adding;
      b.classList.remove('is-loading', 'is-added');
      if (b.dataset.originalHtml) { b.innerHTML = b.dataset.originalHtml; delete b.dataset.originalHtml; }
    });
  };
  window.addEventListener('pageshow', resetAddButtons);

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-cart-upsell-form]');
    if (!form) return;
    e.preventDefault();
    // getAttribute('id'), NOT form.id: these forms contain a <select name="id">
    // for the variant, and a form control named `id` shadows the element's own
    // id property. form.id returned the <select>, so the fallback selector was
    // garbage and the 'Comprado juntamente' buttons (which live OUTSIDE their
    // form, wired by form="...") were never found -- no loading state, no
    // success beat.
    const formId = form.getAttribute('id');
    const btn = form.querySelector('.cart-upsell-card__add, .card-fadd__btn') || (formId && document.querySelector('button[form="' + formId + '"]'));
    // Both button kinds show the same swept fill rather than a text swap:
    // a size chip is too narrow for one, and replacing the upsell label lost
    // the word you clicked. See .is-loading in base.css.
    const id = parseInt(new FormData(form).get('id'), 10);
    // A re-entrancy guard, NOT btn.disabled. Disabling the control (or
    // pointer-events:none) moves the hit-test target out from under a
    // stationary pointer, and Chrome does not repaint the cursor when the
    // target changes without the mouse moving -- the pointer flicked to an
    // arrow for the length of the request, right after the click. Guarding
    // in JS leaves the button as the hit target so the cursor never changes.
    if (btn) {
      if (btn.dataset.adding) return;
      btn.dataset.adding = '1';
      btn.classList.add('is-loading');
    }
    const restore = (text) => {
      if (!btn) return;
      delete btn.dataset.adding;
      btn.classList.remove('is-loading');
      if (text) { btn.dataset.originalHtml = btn.innerHTML; btn.textContent = text; }
      else if (btn.dataset.originalHtml) { btn.innerHTML = btn.dataset.originalHtml; delete btn.dataset.originalHtml; }
    };
    try {
      const add = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: id, quantity: 1 }] }) });
      if (!add.ok) {
        restore('Agotado');
        setTimeout(() => restore(), 2500);
        return;
      }
      // Landed: hand the button its success beat before the re-render can
      // replace it. On the drawer's own cards the swap wipes it almost at
      // once; on a 'Comprado juntamente' row (outside the drawer) it plays in
      // full.
      if (btn) {
        // disabled MUST be cleared here, not only in restore() (the failure
        // path). A disabled <button> ignores cursor:pointer and paints the
        // default arrow, so a chip left disabled after a successful add
        // looks live but reads as dead -- and bfcache carries that state
        // across a back-navigation, which is how it surfaced: add a size on
        // a collection page, open a product, go back, and every chip you
        // had used hovered with the default cursor.
        delete btn.dataset.adding;
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        setTimeout(() => { if (btn) btn.classList.remove('is-added'); }, 620);
      }
      // No early totals pass here. An add MUST re-render — a new row has to
      // appear — so letting a /cart.js update start the bar moving first
      // just put a 46KB innerHTML parse in the middle of that animation.
      // The re-render is the single driver: it lands, then the replay
      // observer animates the bar on a free main thread.
      await refreshCartDrawer();
      openDrawerIfRequested(form);
      // Drive the bar from the freshly rendered value rather than trusting
      // the mutation observer to notice. This path never calls the totals
      // patch, so the observer was the only trigger — and if it missed the
      // swap, adding from the drawer's own upsell cards animated nothing.
      // markFsbComplete is idempotent, so whichever spots it first wins.
      const cBar = document.querySelector('cart-drawer .free-shipping-bar');
      const cScale = fsbScaleOf(cBar && cBar.querySelector('[data-fsb-fill]'));
      if (cScale !== null) {
        const cDir = markFsbComplete(cScale >= 1);
        if (cDir === 'up') {
          setTimeout(() => {
            celebrateFsb(cBar);
            const tx = cBar.querySelector('.fsb__text');
            if (tx && !fsbSwapBusy(tx)) {
              tx.classList.remove('fsb__text--in');
              void tx.offsetWidth;
              tx.classList.add('fsb__text--in');
              setTimeout(() => tx.classList.remove('fsb__text--in'), 640);
            }
          }, 300);
        } else if (cDir === 'down') {
          setTimeout(() => regressFsb(cBar), FSB_KNOB_EXIT_MS);
        }
      }
      restore();
    } catch (err) {
      restore();
    }
  });
})();

	document.addEventListener('DOMContentLoaded', function() {
	  function isIE() {
		const ua = window.navigator.userAgent;
		const msie = ua.indexOf('MSIE ');
		const trident = ua.indexOf('Trident/');

		return (msie > 0 || trident > 0);
	  }

	  if (!isIE()) return;
	  const cartSubmitInput = document.createElement('input');
	  cartSubmitInput.setAttribute('name', 'checkout');
	  cartSubmitInput.setAttribute('type', 'hidden');
	  document.querySelector('#cart').appendChild(cartSubmitInput);
	  document.querySelector('#checkout').addEventListener('click', function(event) {
		document.querySelector('#cart').submit();
	  });
	});
